"use client";

import React, { FC, PropsWithChildren } from "react";
import {
  GlobalStateProvider,
  ShaApplicationProvider,
  StoredFilesProvider,
  useNextRouter,
} from "@shesha-io/reactjs";
import { AppProgressBar } from "next-nprogress-bar";
import { useTheme } from "antd-style";
import { BrandPlugin } from "@/definitions/brand/BrandPlugin";
import { CustomComponentsPlugin } from "@/providers/customComponentsPlugin";
import { APPLICATION_KEY } from "@/app-constants";

export interface IAppProviderProps {
  backendUrl: string;
}

/**
 * The public portal's provider stack.
 *
 * NO TENANT PLUMBING. This portal used to resolve a brand to an ABP tenant id and stamp
 * `Abp.TenantId` onto every request through three separate channels — `buildHttpRequestHeaders`, an
 * axios request interceptor, and a monkey-patched `window.fetch` — because form scripts and component
 * data-sources each bypassed the one before it.
 *
 * Multitenancy has since been retired in favour of the `Forum` discriminator (see
 * `RefListForumType`): PCF/PBF is now a column on Membership, Application and Tier rather than a
 * tenant, the pcf/pbf tenants are deactivated, and row-level scoping is done server-side by
 * `ForumScopeResolver`. There is no tenant to resolve and no header to stamp, so all three channels
 * and `useTenantResolver` are gone.
 *
 * The admin portal still carries the old plumbing; it is being handled separately. This divergence is
 * deliberate.
 */
export const AppProvider: FC<PropsWithChildren<IAppProviderProps>> = ({
  children,
  backendUrl,
}) => {
  const nextRouter = useNextRouter();
  const theme = useTheme();

  return (
    <GlobalStateProvider>
      <AppProgressBar height="4px" color={theme.colorPrimary} shallowRouting />
      <ShaApplicationProvider
        backendUrl={backendUrl}
        router={nextRouter}
        // Must match the app_key seeded by migration M20260805080928 in frwk.front_end_apps.
        // Scopes this portal's menu/theme/settings separately from the admin portal (default-app).
        applicationKey={APPLICATION_KEY}
        noAuth={nextRouter.path?.includes("/no-auth")}
      >
        <BrandPlugin />
        <CustomComponentsPlugin>
          <StoredFilesProvider baseUrl={backendUrl} ownerId={""} ownerType={""}>
            {children}
          </StoredFilesProvider>
        </CustomComponentsPlugin>
      </ShaApplicationProvider>
    </GlobalStateProvider>
  );
};
